<script lang="ts" setup>
import { IMarket } from "~/types/sports/market";
import { IOutcome } from "~/types/sports/outcome";

interface Props {
	outcomes: IOutcome[];
	market: IMarket;
}

const props = defineProps<Props>();

const groupedHandicapsOutcomes = computed(() => {
	return Object.groupBy(props.outcomes, (outcome) => {
		return outcome.name.split("&")[0];
	});
});

const doesHeaderAlignWithRow = (outcome: IOutcome): boolean | undefined => {
	const firstOutcomeName = props.outcomes?.[0]?.name;

	if (!firstOutcomeName || !outcome.name || props.outcomes.length <= 7) return;

	const [, firstSuffix] = firstOutcomeName.split("&");
	const [, currentSuffix] = outcome.name.split("&");

	return firstSuffix === currentSuffix;
};
</script>

<template>
	<div class="p-1 rounded-b-lg shadow-sm bg-light-50 gap-1 dark:bg-dark-800">
		<div v-for="(groupOutcomes, name, index) in groupedHandicapsOutcomes" :key="`combo-outcome-${name}-${index}`">
			<!-- Header Row -->
			<div
				v-if="index === 0 && groupOutcomes"
				class="grid items-center gap-2 w-1/2 mb-[2px] float-right"
				:class="`grid-cols-${groupOutcomes.length}`"
			>
				<div v-for="(element, headerIndex) in groupOutcomes?.length" class="text-center leading-tight text-xs">
					{{ groupOutcomes[headerIndex].name.split("&")[1] }}
					<span v-if="market.handicap">({{ market.handicap }})</span>
				</div>
			</div>

			<!-- Outcome Row -->
			<div v-if="groupOutcomes" class="mt-auto p-[2px] items-center">
				<div class="grid grid-cols-2 items-center gap-1 w-full text-ellipsis">
					<div class="text-xs leading-tight">
						{{ name }}
					</div>
					<div :class="`grid gap-2 grid-cols-${outcomes?.length > 7 ? '2' : groupOutcomes?.length}`">
						<div
							v-for="outcome in groupOutcomes"
							:key="outcome.outcomeId"
							:class="[
								'w-full grid grid-cols-1 place-items-center',
								!doesHeaderAlignWithRow(outcome) && outcomes?.length > 7 && 'col-start-2',
								groupOutcomes.length === 1 && outcomes?.length <= 7 && 'col-span-3',
							]"
						>
							<div class="w-full max-w-full mt-auto">
								<slot :outcome="outcome" />
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
